"use client";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { getWizardDefinition } from "@/app/wizard/wizard-engine";
import { StepDef } from "@/app/wizard/wizard-types";
import { getCachedAxiosLocale } from "@/libs/axios-cms-client";
import { useCurrentLocale } from "@/libs/i18n/utils.client";

/**
 * Hook for accessing wizard translations in client components
 *
 * Lookup order:
 * 1. Step-specific translations (current.translations)
 * 2. Root-level shared translations (wizard.translations)
 * 3. Fallback string (provided by caller)
 *
 * @example
 * function MyComponent() {
 *   const { t } = useWizardTranslation();
 *
 *   return (
 *     <button>
 *       {t('validation_modal_confirm', 'Confirm')}
 *     </button>
 *   );
 * }
 */
export function useWizardTranslation() {
  const { wizard, current } = useWizard();
  const locale = useCurrentLocale();

  /**
   * Translate a key to the current locale
   *
   * @param key - Translation key (e.g., 'validation_modal_confirm', 'errors_400')
   * @param fallback - English fallback text
   * @returns Translated string or fallback
   */
  const t = (key: string, fallback?: string): string => {
    // 1. Try step-specific translation first
    const stepTranslation = current.translations?.[locale]?.[key];
    if (stepTranslation) {
      return stepTranslation;
    }

    // 2. Try root-level shared translation
    const sharedTranslation = wizard.translations?.[locale]?.[key];
    if (sharedTranslation) {
      return sharedTranslation;
    }

    // 3. Return fallback or key
    return fallback || key;
  };

  return { t };
}

/**
 * Get wizard translation in client-side non-React contexts
 *
 * Use this function when you can't use React hooks (e.g., Redux thunks,
 * axios interceptors, event handlers).
 *
 * Lookup order:
 * 1. Step-specific translations (if current step provided)
 * 2. Root-level shared translations
 * 3. Fallback string
 *
 * @param key - Translation key (e.g., 'api_error_generic_title')
 * @param current - Optional current step for step-specific translations
 * @param fallback - English fallback text
 * @returns Promise<string> - Translated string or fallback
 *
 * @example
 * // In a Redux thunk
 * import { getWizardTranslation } from '@/libs/i18n/use-wizard-translation';
 *
 * const title = await getWizardTranslation(
 *   'api_error_generic_title',
 *   undefined,
 *   'Something went wrong'
 * );
 *
 * @example
 * // With current step
 * const label = await getWizardTranslation(
 *   'validation_modal_confirm',
 *   currentStep,
 *   'Confirm'
 * );
 */
export async function getWizardTranslation(
  key: string,
  current?: StepDef,
  fallback?: string
): Promise<string> {
  try {
    // Fetch wizard definition (client-compatible)
    const wizard = await getWizardDefinition(null);
    const locale = getCachedAxiosLocale();

    // 1. Try step-specific translation first (if current step provided)
    if (current?.translations?.[locale]?.[key]) {
      return current.translations[locale][key];
    }

    // 2. Try root-level shared translation
    const translation = wizard.translations?.[locale]?.[key];
    if (translation) {
      return translation;
    }

    // 3. Return fallback or key
    return fallback || key;
  } catch (error) {
    console.error("[getWizardTranslation] Error:", error);
    return fallback || key;
  }
}
